import { useMemo } from "react";
import {
  SubmitHandler,
  useForm,
  useFormState,
  useWatch,
} from "react-hook-form";

import { notify } from "../../../../components/Notification";
import { FormPanel, discardCopyOf } from "../../../../components/FormPanel";
import {
  buildDraftDetails,
  ConfirmDetail,
} from "../../../../components/PopUp/confirmShared";
import { useResponsive } from "../../../../hooks/useResponsive";
import { BREAKPOINTS } from "../../../../utils/constants";
import { DateFormat, formatDate, formatPercent } from "../../../../utils";
import {
  useCreateInterestRateMutation,
  useUpdateInterestRateMutation,
} from "../../../../services";
import { RATE_TYPE_OPTIONS } from "../../constants";
import { LiabilityRateTypes } from "../../types";
import { LiabilityInterestRatePayload } from "../updateLiability";
import { SetInterestRateFields } from "./SetInterestRateFields";
import { FORM_ID, RESET_FREQUENCY_OPTIONS } from "./constants";
import {
  ModeCopy,
  SetInterestRateMode,
  SetInterestRateObject,
  SetInterestRateProps,
} from "./types";
import { currentRateOf } from "../../helpers";

const MODE_COPY: Record<SetInterestRateMode, ModeCopy> = {
  set: {
    eyebrow: "Set Interest Rate",
    mobileTitle: "Set interest rate",
    actionLabel: "Save",
    successMessage: "Interest Rate Updated",
  },
  edit: {
    eyebrow: "Edit Interest Rate",
    mobileTitle: "Edit interest rate",
    actionLabel: "Save",
    successMessage: "Interest Rate Updated",
  },
  duplicate: {
    eyebrow: "Duplicate Interest Rate",
    mobileTitle: "Duplicate interest rate",
    actionLabel: "Save",
    successMessage: "Interest Rate Added",
  },
};

export const SetInterestRate = ({
  id,
  liability,
  isOpen,
  onClose,
  mode = "set",
  rate,
}: SetInterestRateProps) => {
  const isCompact = useResponsive(BREAKPOINTS.tabletLarge);
  const [createInterestRateTrigger] = useCreateInterestRateMutation();
  const [updateInterestRateTrigger] = useUpdateInterestRateMutation();

  const copy = MODE_COPY[mode];
  const isEdit = mode === "edit";

  const currentEntry = currentRateOf(liability.interestRateHistory) ?? null;
  const currentRate = currentEntry ? currentEntry.interestRate : null;
  const currentRateLabel = formatPercent(currentRate, 3);

  const defaultValues = useMemo<SetInterestRateObject>(() => {
    // Edit / duplicate seed from the selected period; set seeds from the current rate.
    const source = rate ?? currentEntry;
    const rateTypeOption =
      RATE_TYPE_OPTIONS.find((option) => option.label === source?.rateType) ??
      RATE_TYPE_OPTIONS[0];
    const resetSource = rate?.resetFrequency ?? liability.resetMonths;
    const resetOption =
      RESET_FREQUENCY_OPTIONS.find(
        (option) => option.value === String(resetSource),
      ) ?? null;
    const seededRate = rate ? rate.interestRate : currentRate;
    // Editing keeps the original effective date; duplicating starts today.
    const effectiveDate =
      isEdit && rate ? new Date(rate.effectiveDate) : new Date();
    const seededPayment =
      rate?.paymentAmount !== null && rate?.paymentAmount !== undefined
        ? String(rate.paymentAmount)
        : "";

    return {
      rateType: rateTypeOption,
      interestRate: String(seededRate ?? ""),
      effectiveDate,
      resetFrequency: resetOption,
      paymentAmount: seededPayment,
    };
  }, [rate, currentEntry, liability.resetMonths, currentRate, isEdit]);

  const form = useForm<SetInterestRateObject>({
    defaultValues,
    mode: "onBlur",
    reValidateMode: "onChange",
  });
  const { control, reset, getValues } = form;

  const { isSubmitting } = useFormState({ control });

  const selectedRateType = useWatch({ control, name: "rateType" });
  const isVariableRate =
    selectedRateType?.label === LiabilityRateTypes.VARIABLE;

  const onSubmit: SubmitHandler<SetInterestRateObject> = async (data) => {
    const payload: LiabilityInterestRatePayload = {
      effectiveDate: formatDate(data.effectiveDate),
      interestRate: Number.parseFloat(data.interestRate),
      rateType: data.rateType.label as LiabilityRateTypes,
    };
    if (isVariableRate && data.resetFrequency) {
      payload.resetFrequency = Number.parseInt(data.resetFrequency.value, 10);
    }
    if (data.paymentAmount) {
      payload.paymentAmount = Number.parseFloat(data.paymentAmount);
    }

    const request =
      isEdit && rate
        ? updateInterestRateTrigger({ id, interestRateId: rate.id, payload })
        : createInterestRateTrigger({ id, payload });

    try {
      await request.unwrap();
      notify.success(copy.successMessage);
      onClose();
      reset(defaultValues);
    } catch {
      // Keep the panel open on failure; the global middleware shows the toast.
    }
  };

  const buildDiscardDetails = (): ConfirmDetail => {
    const draft = getValues();
    const rateValue = Number.parseFloat(draft.interestRate);
    const rateLabel = Number.isNaN(rateValue)
      ? undefined
      : formatPercent(rateValue, 3);
    const dateLabel = draft.effectiveDate
      ? formatDate(draft.effectiveDate, DateFormat.DMY_SPACE)
      : undefined;

    if (isEdit) {
      return {
        pill: { label: "INTEREST RATE", variant: "DEFAULT" },
        title: liability.name,
        subtitle: dateLabel,
        amount: rateLabel,
        amountVariant: "neutral",
      };
    }

    return buildDraftDetails({
      noun: "interest rate",
      fieldValues: [
        draft.interestRate,
        draft.effectiveDate,
        draft.paymentAmount,
      ],
      amount: rateLabel,
      primaryLabel: liability.name,
    });
  };

  const discard = {
    ...discardCopyOf(isEdit, "interest rate"),
    details: buildDiscardDetails,
  };

  const fields = (
    <SetInterestRateFields
      control={control}
      isSubmitting={isSubmitting}
      isVariableRate={isVariableRate}
      isCompact={isCompact}
      currentRateLabel={currentRateLabel}
    />
  );

  return (
    <FormPanel
      open={isOpen}
      onClose={onClose}
      ariaLabel="Set interest rate"
      eyebrow={copy.eyebrow}
      title={liability.name}
      form={form}
      resetValues={defaultValues}
      formId={FORM_ID}
      onSubmit={onSubmit}
      submitLabel={copy.actionLabel}
      discard={discard}
      mobile={{
        title: copy.mobileTitle,
        submitLabel: copy.actionLabel,
        fields,
      }}
    >
      {fields}
    </FormPanel>
  );
};
